import Model from './model.js';
import LeaveType from './leave-type.js';
import LeaveTypes from 'newCollections/leave-types.js';
import Attachment from 'newModels/attachment.js';
import Employee from './employee.js';
import moment from 'moment';
import axios from 'axios';
import Log from 'lib/log';
import Decimal from 'decimal.js';

const log = Log('newModels/leave-request');

export const statuses = {
    1: 'Awaiting approval',
    2: 'Approved',
    3: 'Rejected',
    4: 'Cancelled'
};

export const consts = {
    DATE_TYPE_FULL_DAY: 1,
    DATE_TYPE_MORNING: 2,
    DATE_TYPE_AFTERNOON: 3,
    STATUS_AWAITING: 1,
    STATUS_APPROVED: 2,
    STATUS_REJECTED: 3,
    STATUS_CANCELLED: 4
};

export default class LeaveRequest extends Model {
    urlRoot() { return '/LeaveRequests'; }

    constraints() {
        return {
            typeId: {
                presence: {
                    message: '^Please choose a Leave Type'
                }
            },
            dateFrom: {
                presence: {
                    message: '^Please choose a date'
                }
            },
            employeeId: {
                presence: {
                    message: '^Please choose an employee'
                }
            }
        };
    }

    constructor() {
        super(...arguments);
        this.type = null;
    }

    initialize() {
        this.idAttribute = 'objectId';
        this.on('change:typeId', this._setType, this);
        return super.initialize(...arguments);
    }

    getType() {
        if (this.type) {
            return this.type;
        }
        return this._setType(this.get('typeId'));
    }

    _setType(id) {
        if (!app.leaveTypes) {
            log.warn(`App hasn't any leave types loaded`);
            this.type = null;
            return;
        }

        if (!id) {
            log.warn(`Leave request doesn't have a leave type`);
            this.type = null;
            return;
        }

        this.type = app.leaveTypes.find(model => {
            return model.id === id;
        });
        return this.type;
    }

    fetchType() {
        if (this.type && this.type.id === this.get('typeId')) {
            return Promise.resolve(this.type);
        }

        const type = app.leaveTypes.find(
            t => t.id === this.get('typeId')
        );
        if (type) {
            this.type = type;
            return Promise.resolve(type);
        }

        return new LeaveType({objectId: this.get('typeId')}).fetch()
            .then(model => {
                this.type = model;
                return this.type;
            });
    }

    setEmployee(employee, options = {}) {
        this.employee = employee;
        return this.set({employeeId: employee.get('objectId')}, options);
    }

    getEmployee() {
        return this.employee;
    }

    fetchEmployee() {
        if (this.employee && this.employee.id === this.get('employeeId')) {
            return Promise.resolve(this.employee);
        }

        const employee = app.employees.find(
            emp => emp.id === this.get('employeeId')
        );
        if (employee) {
            this.employee = employee;
            return Promise.resolve(employee);
        }

        return new Employee({objectId: this.get('employeeId')}).fetch()
            .then(model => {
                this.employee = model;
                return this.employee;
            });
    }

    asCalendarEvent() {
        return {
            start: moment.utc(this.get('dateFrom')),
            allDay: !this.get('dateTo'),
            end: this.get('dateTo') ?
                moment.utc(this.get('dateTo')) :
                moment.utc(this.get('dateFrom')),
            model: this
        };
    }

    getStatusText() {
        const status = this.get('status');
        return statuses[status] || '';
    }

    getStatusColor() {
        const status = this.get('status');
        const leaveRequestMap = {
            1: 'text-warning',
            2: 'text-success',
            3: 'text-danger'
        };
        return leaveRequestMap[status] || '';
    }

    isAwaiting() {
        return this.get('status') === consts.STATUS_AWAITING;
    }

    isApproved() {
        return this.get('status') === consts.STATUS_APPROVED;
    }

    isRejected() {
        return this.get('status') === consts.STATUS_REJECTED;
    }

    isCancelled() {
        return this.get('status') === consts.STATUS_CANCELLED;
    }

    hasApprovals() {
        return this.get('approvalStatus') > 0;
    }

    dateFromIsMorning() {
        return this.get('dateFromType') === consts.DATE_TYPE_MORNING;
    }

    dateFromIsAfternoon() {
        return this.get('dateFromType') === consts.DATE_TYPE_AFTERNOON;
    }

    dateToIsMorning() {
        return this.get('dateToType') === consts.DATE_TYPE_MORNING;
    }

    getRequestedDays() {
        return this.get('daysRequired');
    }

    getFormattedDateFrom() {
        return moment.utc(this.get('dateFrom')).format('D MMM YYYY');
    }

    getFormattedDateTo() {
        return this.get('dateTo') ?
            moment.utc(this.get('dateTo')).format('D MMM YYYY')
            : this.getFormattedDateFrom();
    }

    approve() {
        const url = `${this.finalUrl()}/approve`;
        return axios.post(url, {}, {
            headers: {'Authorization': app.user.getAccessToken().id}
        }).then(() => this.fetch());
    }

    cancel() {
        return this.save({status: consts.STATUS_CANCELLED});
    }

    canBeAdded() {
        const url = `${this.baseUrl()}/canBeAdded`;
        return axios.get(url, {
            params: {
                employeeId: this.get('employeeId'),
                leaveTypeId: this.get('typeId'),
                dateFrom: this.get('dateFrom'),
                dateFromType: this.get('dateFromType'),
                dateTo: this.get('dateTo'),
                dateToType: this.get('dateToType')
            },
            headers: {'Authorization': app.user.getAccessToken().id}
        }).then(r => r.data.result);
    }

    canBeCancelled() {
        const url = `${this.finalUrl()}/isCancellable`;
        return axios.get(
            url,
            {headers: {'Authorization': app.user.getAccessToken().id}}
        ).then(r => r.data.result);
    }

    fetchDaysAllowed() {
        const employeeId = this.get('employeeId');
        const type = this.getType();

        if (!employeeId || !type) {
            return Promise.resolve(0);
        }

        return (
            this.getEmployee() ?
            Promise.resolve(this.getEmployee()) :
            this.fetchEmployee()
        ).then(employee =>
            new LeaveTypes([type], {company: null})
                .fetchBalances(employee, this.get('dateFrom'))
        ).then(() => {
            const daysAllowed = new Decimal(type.getEntitled())
                .minus(type.getTaken())
                .toDP(1, Decimal.ROUND_DOWN)
                .toNumber();
            return Promise.resolve(daysAllowed);
        });
    }

    createAttachment({file, name, description}) {
        const attachment = new Attachment({
            name,
            description,
            file: {
                name,
                content: file
            },
            tags: ['leaveRequest'],
            attachableId: this.id,
            attachableType: 'LeaveRequest'
        });

        return attachment.save();
    }

    getLink() {
        return `#calendar/${this.id}/view`;
    }
}



// WEBPACK FOOTER //
// ./app/new-models/leave-request.js